import * as React from "react";
import { Form, Select } from "antd";
import { ObjectO2 } from "src/pages/ManagementPage/Obj2Tab/Obj2Nodes";
import { ZOption } from "src/types/ZOption";
import { filterOptionByName } from "src/common/filterOptionByName";
import styles from "./BsObjectSection.module.less";
import { AttrDef, BsAttrSection } from "./BsAttrSection";

/**
 * Общий компонент для форм настроек различных бизнес-сервисов
 * Позволяет выбирать объект и атрибуты для этого объекта
 *  Объект
 * ┌───────────────────────────────────────────┐
 * │ Объект                                    │
 * └───────────────────────────────────────────┘
 *  Дата начала           Дата окончания
 * ┌───────────────────┐ ┌─────────────────────┐
 * │ Дата начала       │ │ Дата окончания      │
 * └───────────────────┘ └─────────────────────┘
 *
 * Список объектов (objList) берётся из самой верхней ветки в дереве админки  (где показаны объекты, их атрибуты и прочее).
 * Это свойство treeData
 *
 * При изменении объекта:
 * - очищаются все принадлежащие ему поля с атрибутами
 * - для полей загружаются новые списки опций из атрибутов нового объекта
 * - выполняется автоподстановка (для обязательных полей).
 *
 * Автоподстановка использует доступные данные.
 * Например, если в списке только одна опция, то она сразу подставляется в поле, т.к. других вариантов нет.
 * Иначе выполняется поиск совпадений между лейблами поля и опции.
 * Например, поле называется "Дата начала" и в списке есть опция "Дата начала".
 */

type PropsBsObjectSection = {
  objName: string; // Имя поля в структуре данных, которое содержит objectId
  objLabel: string;
  objList: ObjectO2[];
  attrs?: AttrDef[];
};

export const BsObjectSection = (
  props: PropsBsObjectSection,
): React.ReactNode => {
  const { objName, objLabel, objList, attrs } = props;
  const objOptions: ZOption[] = React.useMemo(
    () =>
      objList.map(({ object }) => ({
        value: String(object.id),
        label: object.name,
      })),
    [objList],
  );
  const form = Form.useFormInstance();
  const curObjectId: string | undefined = Form.useWatch(objName);
  const curObject: ObjectO2 | undefined = React.useMemo(
    () => objList.find(({ object }) => object.id === curObjectId),
    [curObjectId],
  );
  const onObjChange = () => {
    attrs?.forEach(({ attrName }) => form.setFieldValue(attrName, null));
  };
  return (
    <>
      <Form.Item name={objName} label={objLabel} rules={[{ required: true }]}>
        <Select
          allowClear
          options={objOptions}
          showSearch
          filterOption={filterOptionByName}
          onChange={onObjChange}
          placeholder="Выбрать"
        />
      </Form.Item>
      {!!attrs?.length && (
        <div className={styles.attrBox}>
          {attrs
            .filter(({ hidden }) => !hidden)
            .map(({ CustomRender = BsAttrSection, ...attrProps }) => (
              <CustomRender
                key={attrProps.attrName}
                attrDef={attrProps}
                curObject={curObject}
              />
            ))}
        </div>
      )}
    </>
  );
};
